/**
 * Ed 汇报流数据 adapter —— 接口 + 领域类型（fe-spec-today-summary §2.1/§2.3）。
 *
 * V3.0.7 现成接口拼装：装配点已切 trackerListAdapter（listTrackers/getGoal/getTracker/
 * listTrackerRecords/updateTracker 拼装）；mock 实现（reportDataAdapter.mock）保留供测试。
 * 运行时依赖（trackerApi/ownerId/market 未读判定）只能由 hook 层提供 —— 消费方经
 * useReportAdapterDeps 注入各方法 deps 参数（消费方 query 层之外的拼装层不可达 hook）。
 */
import type { TrackerClient } from "@features/api/TrackerApi";
import type { GoalImpactData } from "@components/fitness/types";
import { trackerListAdapter } from "./trackerListAdapter";

/** —— 摘要条聚合（现成接口拼装期前端同源派生；服务端 GAP-1 就绪后收回服务端）—— */
export type BriefReportSummary = {
  /** = 汇报页数 = 开场页三块之和（三处恒等，同源） */
  unread_count: number;
  /** 上次查看汇报后新出现未读任务数；0 → 无徽标 */
  new_count: number;
  /** unread = unread_count≥1 且当日未进过汇报；new_since = 当日已进过汇报后又有新到（PRD A.1）。
   *  现成接口拼装期由本地 watermark 派生（reportSummaryWatermark）；服务端就绪后直取下发值。 */
  bar_state: "unread" | "new_since";
  /** 最新未读项标题（含 account read headline，fe-spec §1.3） */
  latest_unread_title?: string;
  /** 最新未读项发布时间（ISO 字符串，用于比较最新） */
  latest_published_at?: string;
};

/** 可见两态直取服务端 bar_state；hidden = unread_count=0 或取数失败/超时（前端派生） */
export type SummaryBarState = BriefReportSummary["bar_state"] | "hidden";

/** —— 汇报组内容（一次给全）—— */
export type ReportTaskKind = "goal" | "tracker";

export type ReportTaskItem = {
  /** goal_id / tracker_id（一个任务一页的去重键） */
  task_id: string;
  kind: ReportTaskKind;
  /** 该任务最新一条未读 signal 卡面标题（开场页摘要行；取不到 → 该行不渲染） */
  latest_unread_title: string;
  /** 该任务最新未读 signal 时间（ISO 串；eyebrow relativeTime 数据源，S5/S6 消费；
   *  取不到由消费方只渲染左段兜底） */
  last_signal_at?: string;
  /** 该任务当前全部未读（页入参 + 已读落定范围）；
   *  现成接口拼装期成组阶段无 record 明细（listTrackers 不含），恒 []，消费方不依赖 */
  unread_signal_ids: string[];
  /** goal 任务：组内全部 tracker ids（goal 级已读落定 = 逐 tracker updateTracker 的入参；
   *  成组时已知，随组透传；tracker 任务不填） */
  tracker_ids?: string[];
};

export type ReportMarketSummaryRef = {
  /** yyyyMMdd（内容侧自然日，useDailyMarketReadState().reportDate 同源；markRead 校验该格式） */
  date: string;
  /** 详情页标题（embed 契约） */
  title: string;
  /** 未读时才出现在 group；已读/取不到 = null（不入组不计数） */
  included: true;
};

export type ReportGroup = {
  /** 用户本地自然日 */
  date: string;
  market_summary: ReportMarketSummaryRef | null;
  /** 同类内按最新未读 signal 排序（选卡引擎口径，后端排好） */
  goals: ReportTaskItem[];
  trackers: ReportTaskItem[];
};

/** 开场页三块计数：全部由 group 派生，前端不单独取数 */
export type OpeningCounts = {
  /** = group.goals.length */
  goal_count: number;
  /** = group.trackers.length */
  tracker_count: number;
  /** = group.market_summary ? 1 : 0 */
  market_count: 0 | 1;
};

/** —— goal 汇报页详情（plan 级未读）—— */
export type ReportGoalDetail = {
  goal_id: string;
  /** Hero 标题；取不到 → 该 goal 不进组（S1.8） */
  statement: string;
  /** goal category（GoalCategoryEnum）——embed 顶区 Hero 背景图按此加载（2026-08-29 复用裁定） */
  category?: string;
  /** goal 概述（非汇报态 goal-detail-summary 同源）；空则 Hero 内不渲染 */
  summary?: string;
  progress: {
    current: number;
    target: number;
    unit: string;
    months_left: number;
    pct: number;
    fitness_delta: number;
  };
  /** 生成侧产出；写不出时 headline/body 为 null（只渲染 Day n + 起始行） */
  our_progress: {
    day: number;
    started_on: string;
    headline: string | null;
    body: string | null;
  };
  plans: ReportGoalPlan[];
  /** 分数影响（形状对齐 GoalImpactData，GoalDetailsEmbedPanel 直连 GoalDetailPlanSection.impact；
   *  缺省/取不到 → GoalFitnessImpact 自身不渲染兜底） */
  impact?: GoalImpactData | null;
};

export type ReportGoalPlan = {
  plan_id: string;
  name: string;
  status: "in_progress" | "not_started" | "completed" | "cancelled";
  /** 相对时间由前端格式化 */
  updated_at: string;
  tracker_count: number;
  /** 红点（不带数字） */
  has_unread: boolean;
  /** New signal 行 + 展开区内容 */
  latest_unread: {
    signal_id?: string;
    title: string;
    content_json: unknown;
  } | null;
  /** 展开区无未读时的 tracker 描述 */
  trackers: {
    tracker_id: string;
    name: string;
    description: string | null;
  }[];
};

/** —— Ask Ed 上下文（direct_context 协议原料：signal 页=record 拼装、goal 页=goal 详情、市场页=日期+标题）—— */
export type ReportAskEdContext = {
  /** signal 引用形态（tracker 汇报页）：该任务最新一条未读 signal */
  signal_id?: string;
  signal_type?: string;
  /** question 引用标题（方括号文案）：signal 页=卡面标题；goal 页=goal 标题；市场页=详情页标题 */
  signal_title: string;
  tracker_id?: string;
  goal_id?: string;
  relation_tier?: "goal" | "holding" | "watch";
  /** signal 正文（record title/description/content 拼装；direct_context Report Content 行原料） */
  report_content?: string;
  /** signal 时间（ISO 串；direct_context Date 行原料） */
  signal_at?: string;
  /** goal 页场景 D：plan 名列表（Page: Goal detail 原料） */
  plan_names?: string[];
  /** 市场总结页引用形态：yyyyMMdd（无 signal_id） */
  report_date?: string;
  entry: "report";
  locale: string;
};

/** —— 反馈上报载荷（sendFeedback 已撤接口走埋点；类型保留供 like/undo content_ref 组装与 mock）—— */
export type ReportFeedbackPayload = {
  option_set: "signal" | "daily_summary";
  option_ids: string[];
  detail?: string;
  content_ref: {
    signal_id?: string;
    tracker_id?: string;
    goal_id?: string;
    report_date?: string;
    report_id?: string;
  };
  entry: "report";
};

/** 现成接口拼装期运行时依赖 —— 只能由消费方 hook 层提供（见 useReportAdapterDeps 单点）：
 *  trackerApi 来自 OspApiClientContext、ownerId 来自 rootStore、
 *  market 未读判定/日期/markRead 来自 useDailyMarketReadState（均在 hook 之外不可得）。 */
export type ReportAdapterDeps = {
  trackerApi: TrackerClient<unknown>;
  ownerId: string;
  /** 市场总结未读（useDailyMarketReadState().isUnread）；false → market 不入组不计数 */
  marketUnread: boolean;
  /** 市场总结内容侧自然日 yyyyMMdd；null → market 不入组 */
  reportDate: string | null;
  /** market 日期门控（S1.7）：pending=正文进行中（组挂起等待，≤3s 转 failed）；failed=正文失败且账本
   *  未读（组 query 抛错走页内错误态+重试，而非组空直达结束页——新用户死循环，真机实锤 2026-09-02） */
  marketGate: "ready" | "pending" | "failed";
  /** 市场总结当日已读落定（useDailyMarketReadState().markRead；adapter 不再落库） */
  markDailySummaryRead: (date?: string) => void;
};

/** 详情/Ask Ed 取数依赖（路径 id 自带鉴权，无需 owner/market 判定） */
export type ReportTrackerApiDep = Pick<ReportAdapterDeps, "trackerApi">;

/** 已读落定依赖（updateTracker body 需 owner_id 鉴权） */
export type ReportSettleDep = Pick<ReportAdapterDeps, "trackerApi" | "ownerId">;

export type ReportDataAdapter = {
  /** 摘要条（3s 超时由 query 层统一；unread_count 三块之和单点派生） */
  fetchSummary(deps: ReportAdapterDeps): Promise<BriefReportSummary>;
  /** 汇报组（S1.8 剔除语义：单条内容缺失在成组阶段已剔除，前端拿到即最终组） */
  fetchGroup(date: string, deps: ReportAdapterDeps): Promise<ReportGroup>;
  /** goal 详情（getGoal?enrich_trackers=true 映射） */
  fetchGoalDetail(
    goalId: string,
    deps: ReportTrackerApiDep,
  ): Promise<ReportGoalDetail>;
  /** Ask Ed direct_context 原料（signal 页传 trackerId、goal 页传 goalId、市场页传 reportDate） */
  fetchAskEdContext(
    ref: { trackerId: string } | { goalId: string } | { reportDate: string },
    deps: ReportTrackerApiDep,
  ): Promise<ReportAskEdContext>;
  /** 已读落定：tracker → updateTracker(unread_count=0)；goal → 组内全部 tracker 逐个 updateTracker
   *  （tracker ids 由 group 任务 tracker_ids 透传）；deps 缺失时真实实现抛错（mock 忽略） */
  settleTaskRead(
    task: {
      kind: ReportTaskKind;
      taskId: string;
      unreadSignalIds: string[];
      /** goal 任务组内全部 tracker ids（goal 级落定的实际执行对象） */
      trackerIds?: string[];
    },
    deps?: ReportSettleDep,
  ): Promise<void>;
  /** 市场总结当日已读：真实实现 no-op（消费方经 deps.markDailySummaryRead 本地落定）；保留接口供 mock 期测试 */
  settleDailySummaryRead(date: string): Promise<void>;
  /** 已撤接口（walk 埋点缺省路径）：反馈上报不再经 adapter，提交走 useDetailFeedback 的
   *  brief_feedback_submit 埋点；mock 实现保留（AC18 失败注入测试） */
  sendFeedback?(payload: ReportFeedbackPayload): Promise<void>;
  /** like 后端接口未出设计：埋点已在页面（brief_report_like/undo），真实实现 no-op */
  sendLike(content_ref: ReportFeedbackPayload["content_ref"]): Promise<void>;
  undoLike(content_ref: ReportFeedbackPayload["content_ref"]): Promise<void>;
};

/** 唯一装配点：消费方只 import 此常量，始终走真实 tracker/goal API 拼装。 */
export const reportDataAdapter: ReportDataAdapter = trackerListAdapter;
